/* eslint-disable @typescript-eslint/consistent-type-assertions */

import * as Application from 'expo-application';
import Constants from 'expo-constants';

import { logger } from '@garnish/logger';
import { not, setup } from 'xstate5';

import {
  type Context,
  type Event,
  fetchUpdateIfAvailable,
  Guards,
  type Input,
  restartApp,
} from './model';

import type { GuardPredicate } from 'xstate5/guards';

// ─── Constants ───────────────────────────────────────────────────────────────

const DEFAULT_POLLING_INTERVAL = 20 * 60 * 1000; // 20 minutes
const DEFAULT_LAUNCH_GATE_TIMEOUT = 2000; // 2 seconds
const FETCH_ON_LAUNCH_TIMEOUT = 3000; // 3 seconds
const NATIVE_APP_VERSION = Application.nativeApplicationVersion ?? '-';
const BUNDLE_APP_VERSION = Constants.expoConfig?.version ?? '-';

// ─── Logger ──────────────────────────────────────────────────────────────────

const defaultLogger = logger.extend('OTA_MACHINE');

// ─── Model ───────────────────────────────────────────────────────────────────

const model = setup({
  types: {
    context: {} as Context,
    events: {} as Event,
    input: {} as Input | undefined,
  },
  actors: {
    'fetch-update-if-available': fetchUpdateIfAvailable,
    'restart-app': restartApp,
  },
  guards: {
    'check-if-updates-enabled': Guards.checkIfUpdatesEnabled,
    'check-if-new-update-is-not-available': Guards.checkIfNewUpdateIsNotAvailable, // prettier-ignore
    'check-if-app-is-ready': Guards.checkIfAppIsReady,
    'check-if-should-use-launch-gate': Guards.checkIfShouldUseLaunchGate,
  },
  actions: {
    //
    // ─── External Actions ────────────────────────────────────────────────

    onLaunchGateEntered() {},
    onLaunchGateTimedOut() {},
    onLaunchFetchStarted() {},
    onLaunchFetchNoUpdate() {},
    onLaunchFetchUpdateFound() {},
    onLaunchFetchFailed() {},
    onLaunchFetchTimedOut() {},
    onUpdateFound() {},
    onRestartDeferred() {},
    onRestartStarted() {},
    onRestartSucceeded() {},
    onRestartFailed() {},
  },
  delays: {
    POLLING_INTERVAL: DEFAULT_POLLING_INTERVAL,
    FETCH_ON_LAUNCH_TIMEOUT: FETCH_ON_LAUNCH_TIMEOUT,
    LAUNCH_GATE_TIMEOUT: DEFAULT_LAUNCH_GATE_TIMEOUT,
  },
});

// ─── Machine ─────────────────────────────────────────────────────────────────

/**
 * A state machine designed for managing OTA (over-the-air) updates in an
 * application.
 *
 * Machine transitions through various states from setup and polling to the
 * installation of updates, managing the update's process flow based on async
 * events and guards.
 *
 * States:
 * - setup: Transitional state used to check if OTA updates are enabled and
 *   whether to enter the launch-time install gate.
 * - waiting: Launch-time gate that blocks app rendering until an `enable` or
 *   `disable` event arrives, or the LAUNCH_GATE_TIMEOUT elapses.
 * - fetching-update-on-launch: Async state that checks for an available update
 *   on launch after an `enable` event. Transitions to `restarting` to apply
 *   the update before the rest of the app renders, or to `polling` if no
 *   update is available.
 * - idle: Initial state (when the launch-time gate is disabled) waiting for
 *   triggers to begin polling or handle updates.
 * - polling: State that periodically checks for available updates.
 * - fetching-update-if-available: Async state that uses a service to determine if an update exists.
 * - has-update: Indicates an available update ready to be applied; can transition to restarting.
 * - restarting: Manages the restart necessary to apply the OTA update.
 * - restart-failed: Indicates a failure during restart, requiring manual intervention.
 * - disabled: Final state if OTA updates are not enabled.
 * - done: Final state indicating the successful completion of the update process.
 *
 * Transitions between states are handled based on events such as `enable`,
 * `disable`, `update`, and through invocation of external services to check
 * and apply updates, accompanied by the error handling and state guards.
 */
export const otaMachine = model.createMachine({
  initial: 'setup',
  context: ({ input }) => ({
    logger: input?.logger ?? defaultLogger,
    shouldUseLaunchTimeGate: input?.shouldUseLaunchTimeGate ?? false,
  }),
  states: {
    setup: {
      description:
        'The setup state serves as a transitional state used to determine if OTA updates are enabled and whether the launch-time install gate is active.',
      entry: ({ context }) => {
        context.logger.debug(
          `Native app version: ${NATIVE_APP_VERSION} | Bundle app version: ${BUNDLE_APP_VERSION}`,
        );
      },
      always: [
        {
          target: 'disabled',
          guard: not('check-if-updates-enabled'),
        },
        {
          target: 'waiting',
          guard: 'check-if-should-use-launch-gate',
        },
        {
          target: 'idle',
        },
      ],
    },
    waiting: {
      description:
        'Launch-time gate. Blocks app rendering until a consumer signals readiness via an `enable` (install path) or `disable` (open without installing) event, or until the LAUNCH_GATE_TIMEOUT elapses as a fallback so the app is never stranded behind a slow consumer.',
      entry: 'onLaunchGateEntered',
      on: {
        enable: 'fetching-update-on-launch',
        disable: 'idle',
      },
      after: {
        LAUNCH_GATE_TIMEOUT: {
          target: 'idle',
          actions: 'onLaunchGateTimedOut',
        },
      },
    },
    'fetching-update-on-launch': {
      description:
        'Checks for an available update on launch. When a downloaded update exists, transitions to `restarting` to apply it before rendering the app; otherwise, joins the regular polling loop with the gate open.',
      entry: 'onLaunchFetchStarted',
      invoke: {
        src: 'fetch-update-if-available',
        onDone: [
          {
            target: 'polling',
            guard: {
              type: 'check-if-new-update-is-not-available',
              params: ({ event }) => ({ updateResult: event.output }),
            },
            actions: 'onLaunchFetchNoUpdate',
          },
          {
            target: 'restarting',
            actions: 'onLaunchFetchUpdateFound',
          },
        ],
        onError: {
          target: 'polling',
          actions: [
            'onLaunchFetchFailed',
            ({ event, context }) => context.logger.error(event.error),
          ],
        },
      },
      after: {
        FETCH_ON_LAUNCH_TIMEOUT: {
          target: 'polling',
          actions: [
            'onLaunchFetchTimedOut',
            ({ context }) => context.logger.debug('Fetch on launch timed out, skipping to polling'),
          ],
        },
      },
      on: {
        disable: 'idle',
        'notify-about-update': { target: 'restarting', actions: 'onUpdateFound' },
      },
    },
    idle: {
      description:
        'Serves as the initial state of the OTA update state machine. In this state, the machine is waiting for triggers to start polling for updates or to handle initial update.',
      on: {
        enable: 'polling',
        'notify-about-update': { target: 'has-update', actions: 'onUpdateFound' },
      },
    },
    polling: {
      description:
        'A simple polling state that checks for a possible update based on the configurable time interval.',
      after: {
        POLLING_INTERVAL: 'fetching-update-if-available',
      },
      on: {
        disable: 'idle',
        'notify-about-update': { target: 'has-update', actions: 'onUpdateFound' },
      },
    },
    'fetching-update-if-available': {
      description:
        'An async state that uses the associated service to check for an update. If no update is found, polling is triggered.',
      invoke: {
        src: 'fetch-update-if-available',
        onDone: [
          {
            target: 'polling',
            guard: {
              type: 'check-if-new-update-is-not-available',
              params: ({ event }) => ({ updateResult: event.output }),
            },
          },
          {
            target: 'has-update',
            actions: 'onUpdateFound',
          },
        ],
        onError: {
          target: 'polling',
          actions: [({ event, context }) => context.logger.error(event.error)],
        },
      },
      on: {
        disable: 'idle',
        'notify-about-update': { target: 'has-update', actions: 'onUpdateFound' },
      },
    },
    'has-update': {
      description:
        'A state indicating that there is an update available to be installed. In this state, the machine is ready to initiate the update process, potentially restarting the application to apply the new updates. Transitions to the "restarting" state when an update is triggered.',
      on: {
        disable: 'idle',
        update: [
          { target: 'restarting', guard: 'check-if-app-is-ready' },
          { target: 'has-update', actions: 'onRestartDeferred' },
        ],
      },
    },
    restarting: {
      description:
        'Manages the process of restarting the application to apply an OTA update. Upon successful completion of the restart process, the state transitions to the "done" state, indicating the update process has been successfully completed. If an error occurs during the restart, it transitions to the "restart-failed" state.',
      entry: 'onRestartStarted',
      invoke: {
        src: 'restart-app',
        onDone: {
          target: 'done',
          actions: 'onRestartSucceeded',
        },
        onError: {
          target: 'restart-failed',
          actions: ['onRestartFailed', ({ event, context }) => context.logger.error(event.error)],
        },
      },
    },
    'restart-failed': {
      description: 'A state that notifies the user that a manual restart is necessary.',
    },
    disabled: {
      description:
        'A state indicating that the OTA update is disabled for the current environment. It acts as a final state, and no further actions are taken beyond reaching this state.',
      type: 'final',
    },
    done: {
      description:
        'The final state, indicating that the OTA update process has been successfully completed.',
      type: 'final',
    },
  },
});
